import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from "@/components/ui/button";
import { Menu, X, Phone, User, LogOut, LayoutDashboard, Building, Briefcase } from 'lucide-react';
import { useIsMobile } from '@/hooks/use-mobile';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const isMobile = useIsMobile();
  const { user, signOut, isAdmin, userRole } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 10);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleLoginClick = () => {
    navigate('/login');
    setMobileMenuOpen(false);
  };

  const handleDashboardClick = () => {
    console.log('Dashboard clicked, navigating to:', getDashboardPath());
    navigate(getDashboardPath());
    setMobileMenuOpen(false);
  };

  const handleSignOut = async () => {
    try {
      console.log('Sign out clicked');
      await signOut();
      // Navigation happens in the signOut function in AuthContext
    } catch (error) {
      console.error('Error signing out:', error);
      toast.error('Failed to sign out');
    }
    setMobileMenuOpen(false);
  };

  const getDashboardIcon = () => {
    switch (userRole) {
      case 'admin':
        return <LayoutDashboard className="h-4 w-4" />;
      case 'entrepreneur':
        return <Building className="h-4 w-4" />;
      case 'investor':
        return <Briefcase className="h-4 w-4" />;
      default:
        return <LayoutDashboard className="h-4 w-4" />;
    }
  };

  const getDashboardText = () => {
    switch (userRole) {
      case 'admin':
        return 'Admin Dashboard';
      case 'entrepreneur':
        return 'Business Dashboard';
      case 'investor':
        return 'Investor Dashboard';
      default:
        return 'Dashboard';
    }
  };

  const getDashboardPath = () => {
    switch (userRole) {
      case 'admin':
        return '/admin/dashboard';
      case 'entrepreneur':
        return '/dashboard';
      case 'investor':
        return '/investor/dashboard';
      default:
        return '/';
    }
  };

  return (
    <header className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${scrolled ? 'py-1 glassmorphism' : 'py-1 bg-transparent'}`}>
      <div className="container mx-auto px-4 flex items-center justify-between">
        <div className="flex items-center">
          <Link to="/" className="flex items-center">
            <img src="/uploads/5fd4894b-684c-481f-92ad-df1c1aa2d9db.png" alt="H44S Logo" className="h-[50px] w-auto" />
          </Link>
        </div>

        {!isMobile && (
          <nav className="hidden md:flex items-center space-x-8">
          </nav>
        )}

        <div className="flex items-center">
          {user ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" className="mr-4 hidden md:inline-flex">
                  {getDashboardIcon()}
                  {getDashboardText()}
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuLabel>My Account</DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={handleDashboardClick}>
                  {getDashboardIcon()}
                  <span>{getDashboardText()}</span>
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={handleSignOut} className="text-red-500">
                  <LogOut className="mr-2 h-4 w-4" />
                  Logout
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : (
            <>
              <Button 
                className="mr-4 hidden md:inline-flex bg-gradient-to-r from-h44s-bright-pink via-h44s-teal to-h44s-light-blue hover:opacity-90 text-white shadow-sm hover:shadow-md transition-all duration-300"
                onClick={handleLoginClick}
              >
                Log in
              </Button>
              <Link to="/signup">
                <Button className="bg-gradient-to-r from-h44s-light-blue to-h44s-teal text-white shadow-button button-shine">
                  <User className="mr-2 h-4 w-4" />
                  Sign Up
                </Button>
              </Link>
            </>
          )}
          
          {isMobile && (
            <button 
              className="ml-4 p-1 text-gray-700 md:hidden" 
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            >
              {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          )}
        </div>
      </div>

      {/* Mobile menu */}
      {isMobile && mobileMenuOpen && (
        <div className="md:hidden absolute top-full left-0 right-0 glassmorphism border-t border-gray-100 animate-fade-in">
          <div className="container px-6 py-4 flex flex-col space-y-4">
            {user ? (
              <>
                <Button variant="outline" className="w-full justify-start" onClick={handleDashboardClick}>
                  {getDashboardIcon()}
                  {getDashboardText()}
                </Button>
                <Button variant="destructive" className="w-full justify-start" onClick={handleSignOut}>
                  <LogOut className="mr-2 h-4 w-4" />
                  Logout
                </Button>
              </>
            ) : (
              <>
                <Button className="w-full bg-gradient-to-r from-h44s-bright-pink via-h44s-teal to-h44s-light-blue hover:opacity-90 text-white shadow-sm hover:shadow-md transition-all duration-300" onClick={handleLoginClick}>
                  Log in
                </Button>
                <Link to="/signup" className="w-full" onClick={() => setMobileMenuOpen(false)}>
                  <Button className="w-full bg-gradient-to-r from-h44s-light-blue to-h44s-teal">
                    <User className="mr-2 h-4 w-4" />
                    Sign Up
                  </Button>
                </Link>
              </>
            )}
          </div>
        </div>
      )}
    </header>
  );
};

export default Navbar;
